import { SwitchDeviceIcon } from "@/assets/images/svgIcons/illustrations/switchDeviceIcon"
import { WarningIcon } from "@/assets/images/svgIcons/warningIcon"
import { Button } from "@/components/ui/baseUi/button"
import {
  errorToastClassName,
  infoToastClassName,
  successToastClassName,
  appToast as toast,
} from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { EmailIcon } from "@images/svgIcons/emailIcon"
import { PhoneIcon } from "@images/svgIcons/phoneIcon"
import { BoxInfoResumeHeader } from "@modules/box/components/boxInfoResumeHeader"
import { LocationBox } from "@modules/box/components/locationBox"
import { useBox } from "@modules/box/hooks/useBox"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffersByUser } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferSubscription } from "@modules/boxOffer/hooks/useBoxOfferSubscription"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText, isRichTextLongerThan } from "@modules/ui/components/richText/richTextUtils"
import { formatPhoneNumber } from "@modules/utils/numberUtils"
import { type CountryCode } from "libphonenumber-js"
import { LoaderCircle, Upload } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { useEffect, useMemo } from "react"
import styled from "styled-components"

type BoxInfoResumeProps = {
  boxId: string
} & React.ComponentProps<"div">

export function BoxInfoResume({ boxId, className, ...props }: BoxInfoResumeProps) {
  const canSeeOffers = useFeatureToggle(FeatureKeys.BoxOffersByUser)
  const { result: box } = useBox(boxId)
  const { data: boxOffers } = useBoxOffersByUser(boxId, canSeeOffers)
  const { subscribeToOffer, isProcessing } = useBoxOfferSubscription()
  const { setCustomHeaderButtons } = useSidebar()
  const language = useLanguage()

  useEffect(() => {
    const customHeaderIcons = []

    const canUserShareInvitationLink = box && box.invitationLink

    if (canUserShareInvitationLink) {
      customHeaderIcons.push(
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="outline"
                size="iconMd"
                onClick={() => {
                  if (box.invitationLink) {
                    navigator.clipboard.writeText(box.invitationLink)
                    toast.info(word("box.details.share.success"), {
                      classNames: infoToastClassName(),
                    })
                  }
                }}
              >
                <Upload />
              </Button>
            }
          />
          <TooltipContent side="bottom">
            <Text fontStyle="LM" className="text-textDefault-body">
              {word("sidebar.button.dynamicLink")}
            </Text>
          </TooltipContent>
        </Tooltip>,
      )
    }

    if (customHeaderIcons.length > 0) {
      setCustomHeaderButtons(customHeaderIcons)
    }
  }, [box])

  const richTextDescription = useMemo(() => formatTextToRichText(box?.description, true), [box?.description])

  if (!box) return <LoadingSpinner />

  return (
    <div className={cn(className, "flex flex-col pb-4")} {...props}>
      {/* HEADER */}
      <BoxInfoResumeHeader boxId={boxId} />

      {/* CONTENT */}
      <div className="flex flex-col p-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Text fontStyle="TS" className="mb-2">
              {word("box.info.aboutus")}
            </Text>

            <Text
              fontStyle="BM"
              className="mb-2 line-clamp-4"
              readMore={isRichTextLongerThan({ richText: richTextDescription, numberOfLines: 4, maxLineLength: 70 })}
            >
              <HURichTextView value={richTextDescription} paragrapheFontStyle="BM" />
            </Text>
          </div>
        </div>

        <div className="my-5 flex h-[2px] w-full bg-borderDefault-separator" />

        <ContactContainer>
          <Text fontStyle="TS" className="mb-2">
            {word("box.form.contact.title")}
          </Text>
          <div className="mb-2 flex items-center">
            <Round>
              <EmailIcon color="currentColor" width={15} height={15} className="text-textDefault-heading" />
            </Round>
            <Text fontStyle="BM">{box.email}</Text>
          </div>
          {box.phoneNumberWithPrefix && box.phoneNumberWithPrefix.number && box.phoneNumberWithPrefix.countryCode ? (
            <div className="flex items-center">
              <Round>
                <PhoneIcon color="currentColor" className="text-textDefault-heading" />
              </Round>
              <Text fontStyle="BM">
                {formatPhoneNumber(
                  box.phoneNumberWithPrefix.number,
                  box.phoneNumberWithPrefix.countryCode as CountryCode,
                )}
              </Text>
            </div>
          ) : null}
        </ContactContainer>
        <LocationContainer>
          <Text fontStyle="TS" className="mb-3">
            {word("box.where.address.title")}
          </Text>
          <LocationBox box={box} />
        </LocationContainer>

        {box.invitationLink && (
          <div className="relative mx-3 flex flex-col gap-2 overflow-hidden rounded-4xl bg-neutral-100 p-4">
            <div className="flex items-center justify-center gap-2">
              <WarningIcon color="currentColor" width={24} height={24} />
              <Text fontStyle="TL" color="currentColor">
                {word("boxInfoResume.deviceChange.title")}
              </Text>
            </div>

            <Text fontStyle="BM" color="currentColor" className="mb-3 text-center">
              {word("boxInfoResume.deviceChange.caption")}
            </Text>

            <div className="flex flex-row items-center justify-center gap-6">
              <div className="flex flex-col">
                <Text fontStyle="BM" color="currentColor">
                  {word("boxInfoResume.deviceChange.content")}
                </Text>
                <SwitchDeviceIcon color="currentColor" width={112} height={112} />
              </div>

              <div className="flex">
                <QRCodeSVG value={box.invitationLink} size={170} />
              </div>
            </div>
          </div>
        )}

        {canSeeOffers && boxOffers && boxOffers.length > 0 ? (
          <div className="mt-4 flex flex-col items-center">
            <Text fontStyle="TS" className="mb-3">
              {word("box.offers.page.title")}
            </Text>

            {boxOffers
              .filter((offer) => offer.showcaseDisplay && offer.type === BoxOfferType.Subscription) // Only subscription for now
              .map((offer) => (
                <div className="card flex w-full flex-row items-center justify-between" key={offer.id}>
                  <div className="flex flex-col">
                    <Text fontStyle="BM" className="mb-2">
                      {offer.name}
                    </Text>
                    {offer.priceObject && (
                      <Text fontStyle="BM" className="mb-2">
                        {offer.priceObject.value > 0
                          ? formatCurrency(offer.priceObject.value, offer.priceObject.currency, language)
                          : word("box.member.table.offer.item.freePrice")}
                      </Text>
                    )}
                  </div>
                  {offer.canUserPurchase && offer.autonomousPurchase && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        subscribeToOffer(boxId, offer.id, offer.priceObject?.value === 0)
                          .then(() => {
                            toast.success(word("program.subscribe.toFreeOffer.toast.success"), {
                              classNames: successToastClassName(),
                            })
                          })
                          .catch(() => {
                            toast.error(word("program.subscribe.toFreeOffer.toast.error"), {
                              classNames: errorToastClassName(),
                            })
                          })
                      }}
                    >
                      {isProcessing && <LoaderCircle className="animate-spin" />}
                      {word("program.subscription.subscribe")}
                    </Button>
                  )}
                </div>
              ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const Round = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 39px;
  height: 39px;
  border-radius: 50%;
  background-color: var(--neutral-200);
  margin-right: 10px;
`

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 14px;
  margin-right: 14px;
  margin-bottom: 20px;
`
const LocationContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 14px;
  margin-right: 14px;
  margin-bottom: 20px;
`
